import React, { createContext, useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAssessmentState from '../../lib/hooks/useAssessmentState';
import { postAssessmentStep } from '../api';

export const AssessmentContext = createContext({});

export const AssessmentContextProvider = ({ children, ...props }) => {
  // Grab params and initialize navigation
  const { workflowId: routeWorkflowId, step: routeStep } = useParams();
  const navigate = useNavigate();
  const { data: assessmentState, isLoading, error } = useAssessmentState();

  // Local shared state
  const [currentStepInfo, setCurrentStepInfo] = useState(null);
  const [currentStepChoices, setCurrentStepChoices] = useState(null);
  const [loadingNext, setLoadingNext] = useState(false);
  const [stepError, setStepError] = useState(null);
  const [responses, setResponses] = useState(null);
  const [progress, setProgress] = useState([]);
  const [workflowId, setWorkflowId] = useState(null);
  const [stepOverride, setStepOverride] = useState(null);

  // Derive effective step and workflow id based on override, URL, or assessment state
  const effectiveStep = stepOverride || routeStep || currentStepInfo?.step;
  const effectiveWorkflowId = routeWorkflowId || workflowId;
  const nextAllowed = useMemo(() => currentStepChoices ?? false, [currentStepChoices]);

  // Initialize state from assessmentState
  useEffect(() => {
    if (assessmentState) {
      setCurrentStepInfo(assessmentState.stepInfo || null);
      setProgress(assessmentState.progress || []);
      setResponses(assessmentState.responses || {});
      setWorkflowId(assessmentState.workflow_id);
    }
  }, [assessmentState]);

  // Update state when currentStepInfo changes
  useEffect(() => {
    if (currentStepInfo) {
      setProgress(currentStepInfo.progress || []);
      setResponses(currentStepInfo.responses || {});
      setWorkflowId(currentStepInfo.workflow_id);
    }
  }, [currentStepInfo]);

  // Sync the URL with the effective workflow & step
  useEffect(() => {
    if (effectiveWorkflowId && effectiveStep) {
      const correctUrl = `/assessment/${effectiveWorkflowId}/${effectiveStep}`;
      if (window.location.pathname !== correctUrl) {
        // navigate(correctUrl, { replace: true }); // temp disabled
      }
    }
  }, [effectiveWorkflowId, effectiveStep, navigate]);

  // Scroll to top on step change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStepInfo]);

  // Action: post the next assessment step
  const handleNextStep = async (userResponse) => {
    setLoadingNext(true);
    setStepError(null);
    try {
      const payload = {
        step: currentStepInfo?.step, //effectiveStep || null,
        response: currentStepChoices || { answer: userResponse },
        responses,
        workflow_id: effectiveWorkflowId,
      };
      const data = await postAssessmentStep(payload);
      setCurrentStepInfo(data);
      setResponses(data.responses);
      setProgress(data.progress);
      // Clear any temporary override after posting
      setStepOverride(null);
    } catch (err) {
      console.error('Error posting assessment step:', err);
      setStepError(err);
    } finally {
      setLoadingNext(false);
    }
  };

  // NEW: Auto-trigger the initial step if none is loaded.
  useEffect(() => {
    if (!currentStepInfo && !loadingNext && !stepError) {
      handleNextStep(null);
    }
  }, [currentStepInfo, loadingNext, stepError]);

  // Expose state and actions via context
  return (
    <AssessmentContext.Provider
      value={{
        state: {
          assessmentState,
          currentStepInfo,
          currentStepChoices,
          loadingNext,
          stepError,
          responses,
          progress,
          workflowId,
          stepOverride,
          effectiveStep,
          effectiveWorkflowId,
          nextAllowed,
          isLoading,
          error,
        },
        actions: {
          setCurrentStepInfo,
          setCurrentStepChoices,
          setLoadingNext,
          setStepError,
          setResponses,
          setProgress,
          setWorkflowId,
          setStepOverride,
          handleNextStep,
        },
      }}
      {...props}
    >
      {children}
    </AssessmentContext.Provider>
  );
};
